import React from 'react';
import { Tag, Segmented } from 'antd';

/**
 * The how-to guide, on the interface it explains. Static content — every
 * chip, segment and meter here is rendered with the same components and
 * classes as the real screens, so what you read is what you'll see.
 */

const Step = ({ n, title, children }) => (
  <div className="pm-guide-step">
    <span className="pm-guide-step-n">{n}</span>
    <div>
      <b>{title}</b>
      <div className="pm-guide-step-body">{children}</div>
    </div>
  </div>
);

const Rule = ({ children }) => <li className="pm-guide-rule">{children}</li>;

export default function GuideTab() {
  return (
    <div className="pm-guide">
      <div className="pm-guide-col">
        <section className="pm-guide-card">
          <h4>What this workspace is</h4>
          <p>
            The new plan model, running <b>in parallel</b> with the legacy plan screens.
            The organizing idea: <b>definition is enforcement</b> — the coverage rows,
            pools and prices you configure here are the <i>same rows</i> the system
            enforces at PA, shows enrollees in the brochure, and reports revenue from.
            No parallel copies that can drift.
          </p>
          <p>
            Nothing you do here touches live operations until a plan&apos;s switchboard
            flag is deliberately flipped in <b>Plan Design → Setup</b>. Authoring,
            pricing and publishing are all safe to explore.
          </p>
        </section>

        <section className="pm-guide-card">
          <h4>The lifecycle of a plan</h4>
          <Step n="1" title="Create">
            <b>New plan</b> (top right) → name, plan type, description. The plan is
            created as a <b>draft, non-sellable</b> — it appears nowhere clients or
            organizations can buy it. Or pick an existing plan and{' '}
            <b>Import from legacy limits</b> / <b>Copy design from…</b> another tier.
          </Step>
          <Step n="2" title="Design (Plan Design tab)">
            Set the envelopes, then declare every benefit&apos;s coverage state and caps.
            The rail on the right carries this plan&apos;s <b>Setup</b> and its mapping load.
          </Step>
          <Step n="3" title="Price (Bands & Pricing tab)">
            Pick the bands the plan sells in and set a price per band.
          </Step>
          <Step n="4" title="Publish">
            Plan Design rail → Setup → Status → <b>Published</b>, then <b>Save all</b>.
            Refused while the design is empty.
          </Step>
          <Step n="5" title="Launch (Plan Design → Setup)">
            Makes the plan sellable in its declared segment. Unlocks only when the
            design is published and at least one band is priced.
          </Step>
          <Step n="6" title="Log → Enforce">
            Flip Enforcement to <Tag color="gold" style={{ marginInline: 2 }}>Advisory</Tag>
            first: the plan-model panel appears on PA review and every would-block
            verdict is recorded — <i>nothing blocks</i>. Only after the telemetry and
            nightly reconciliation look clean does a plan go to{' '}
            <Tag color="red" style={{ marginInline: 2 }}>Enforce Strictly</Tag>. Evidence, not dates.
          </Step>
        </section>

        <section className="pm-guide-card">
          <h4>Designer — the schedule grid</h4>
          <p>Every benefit item has exactly one of three states:</p>
          <div className="pm-guide-demo">
            <Segmented
              size="small"
              value="capped"
              options={[
                { label: 'Excluded', value: 'excluded' },
                { label: 'Covered', value: 'uncapped' },
                { label: 'Capped', value: 'capped' },
              ]}
              className="pm-seg-capped"
            />
          </div>
          <ul className="pm-guide-list">
            <li>
              <b>Excluded</b> — not covered. This is also the <b>default</b>: an item
              with no row at all is excluded (&quot;default-deny&quot;). Use an explicit
              Excluded row when you want the denial documented with a note (the
              schedule&apos;s &quot;NA&quot; cells).
            </li>
            <li>
              <b>Covered</b> — no cap of its own; spends against the check-in envelope
              (the schedule&apos;s &quot;Up to Inpatient/Outpatient Limit&quot; rows). Most of a
              real schedule is this — it costs nothing to configure.
            </li>
            <li>
              <b>Capped</b> — opens the cap editor: metric (₦ / count / days /
              quantity), limit, and window. Choose <b>Own pool</b> for a normal cap, or{' '}
              <b>Shared pool</b> to subscribe this benefit to another benefit&apos;s pool —
              that is how &quot;Psychiatric Hospitalization: Up to Accommodation Limit&quot;
              works: two rows, one pool, no copied number that can drift.
            </li>
          </ul>
          <p>
            <b>Windows</b>: policy year (default), calendar month (&quot;4 gym visits per
            month&quot;), or rolling N months (&quot;lenses once in two years&quot; = rolling 24).
            Windows are computed from claim history — renewals don&apos;t reset a rolling
            clock.
          </p>
          <p>
            <b>Adjudication notes</b> (the <span className="pm-note-icon">ⓘ</span> on a
            row) carry human-judged conditions — &quot;SCBU/NICU cases only&quot;, &quot;emergency
            only&quot;. They are shown to the claims officer at PA and{' '}
            <b>never machine-evaluated</b>. If a condition needs judgement, it belongs
            in a note, not a limit.
          </p>
          <p>
            <b>Envelopes</b> (the bold top rows) are the per-check-in-type pools —
            Inpatient ₦2.5M, Outpatient ₦1.1M. Leave the limit empty for unlimited
            (Telemedicine). Types like Optical or Dental often need no envelope at
            all — their items carry their own caps.
          </p>
        </section>

        <section className="pm-guide-card">
          <h4>The rail — enforcement talking back</h4>
          <p>
            As you edit, the right rail recomputes the <b>cardinality rule</b>: for
            each (check-in type, request category) pair, how many covered benefits
            could a request resolve to?
          </p>
          <ul className="pm-guide-list">
            <li>
              <b>Auto-resolving</b> — exactly one candidate. Requests classify
              themselves; no mapping work ever.
            </li>
            <li>
              <b>Mapping required</b> — several candidates. At PA, the claims officer
              picks from that short list once per facility item. This number is the
              workload your design creates for CS — watch it.
            </li>
            <li>
              <b className="pm-guide-warn">Not covered</b> — zero covered candidates
              while the catalog has applicable items. Requests there will be denied
              under enforcement. The rail warns you: make sure it&apos;s intentional.
            </li>
          </ul>
        </section>
      </div>

      <div className="pm-guide-col">
        <section className="pm-guide-card">
          <h4>Bands &amp; Pricing</h4>
          <p>
            Bands (Individual, Family of 2–6…) are a <b>shared master list</b> with no
            prices of their own — a price belongs to the <b>Plan + Band pair</b>.
          </p>
          <p>
            <b>Change price…</b> closes the current history period and opens a new
            one. It <b>never changes what anyone already paid</b>: existing clients
            keep their charged amount (the append-only pricing ledger), and renewals
            pick up the applicable price at renewal time. Org-negotiated overrides win
            where present. That&apos;s the whole pricing model — the confirmation dialog
            restates it every time.
          </p>
        </section>

        <section className="pm-guide-card">
          <h4>Setup — the per-plan switchboard</h4>
          <p>
            Cutover is not a global setting: these flags belong to the plan, so they
            live in the Plan Design rail. All default to legacy/off, each states its
            consequence as you pick it, and nothing is written until <b>Save all</b>.
            Every change is audited and reverses by flipping back:
          </p>
          <ul className="pm-guide-list">
            <li>
              <b>Enforcement</b>: Ignore → <Tag color="gold" style={{ marginInline: 2 }}>Advisory</Tag> →{' '}
              <Tag color="red" style={{ marginInline: 2 }}>Enforce Strictly</Tag>. Advisory is the
              rehearsal: panel + telemetry, zero blocking.
            </li>
            <li>
              <b>Pricing master</b>: legacy → pm. Plan+Band prices become the list
              layer; the legacy price columns become a mirror kept fresh by
              write-back.
            </li>
            <li>
              <b>Brochure source</b>: legacy → pm. Enrollees&apos; package benefits are
              rendered from your coverage rows — what they read is what is enforced.
            </li>
            <li>
              <b>Org plan filter</b>: legacy → plan_type. Retail plans (by declared
              type, never by name) can&apos;t be attached to organizations.
            </li>
          </ul>
          <p>
            <b>Launch plan</b> is separate from the flags: it makes a draft plan
            sellable. It&apos;s the deliberate act that plan creation postpones.
          </p>
        </section>

        <section className="pm-guide-card">
          <h4>Where CS meets this — the PA panel</h4>
          <p>
            Once a plan is in Advisory or Enforce Strictly mode, the PA review screen grows a{' '}
            <b>Plan model check</b> card (side rail): pool positions with the pending
            lines&apos; impact, and per-line verdicts. Lines needing mapping show the
            restricted candidate list — the claims officer picks and clicks <b>Map</b>,
            in the flow, at the moment the choice matters. Everyone else&apos;s PA screen
            is pixel-identical to today.
          </p>
        </section>

        <section className="pm-guide-card">
          <h4>Analytics</h4>
          <p>
            Revenue is the sum of <b>pricing events</b> in the period — what was
            actually charged, live vs backfill segmented — never &quot;current price ×
            active clients&quot;. The dashed tile shows that legacy number purely for
            comparison; it changes whenever a price changes, which is exactly why it&apos;s
            being retired. MLR divides recorded claims cost by recorded premium.
          </p>
        </section>

        <section className="pm-guide-card pm-guide-rules">
          <h4>Rules the system holds (so you don&apos;t have to)</h4>
          <ul className="pm-guide-list">
            <Rule>No coverage row = excluded. Coverage is declared, never assumed.</Rule>
            <Rule>A cap is a pool; sharing a cap = subscribing to the same pool.</Rule>
            <Rule>Price changes never rewrite history — the pricing ledger is append-only.</Rule>
            <Rule>Copy and import need a cleared target (Clear design does it in one click); they never merge.</Rule>
            <Rule>Every flag flip and price change is audited with who and when.</Rule>
            <Rule>Everything fails open: if this subsystem ever errors, legacy behavior continues untouched.</Rule>
          </ul>
        </section>
      </div>
    </div>
  );
}
